import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import { translate } from 'react-i18next';
import L from 'leaflet';

import { GeozoneMap } from './GeozoneMapControls/GeozoneMap';
import { createGeozoneClicked, toggleGeozoneEditor, updateGeozoneClicked } from './actions';
import Modal from '../index';
import Input from '../../common/Input';
import TimesList from './Times/TimesList';
import FloatingLabelInput from '../../common/FloatingLabelInput';
import InputRange from 'react-input-range';
import TimeRangeSlider from 'react-time-range-slider';

const WEEKDAYS_ARRAY = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const convertWeekdaysToState = (weekdays) => {
  const result = {};

  WEEKDAYS_ARRAY.forEach((day, index, array) => {
    if (weekdays[day] && weekdays[day].length) {
      const multi = weekdays[day].filter(({ from, to }) => from && !to);
      if (multi.length) {
        const next = weekdays[WEEKDAYS_ARRAY[index === 6 ? 0 : index + 1]];
        const to = next && next[0] && next[0].to;
        if (to) {
          result[day] = {
            from: multi[0].from,
            to,
          }
        }
      } else if (weekdays[day].length === 1 && !weekdays[array[index ? index - 1 : 6]].some(({ to }) => !to)) {
        result[day] = weekdays[day][0]
      } else if (weekdays[day].length === 2) {
        result[day] = weekdays[day][1]
      }
      if (!result[day] || !result[day].from || !result[day].to) {
        delete result[day]
      }
    }

  });

  return result;
};

const convertGeozoneToState = (geozone) => {
  const days = convertWeekdaysToState(geozone.weekly || {});
  var keys = Object.keys(days)

  const times = keys.length > 0 ? {
    start: days[keys[0]].from,
    end: days[keys[0]].to,
  } : {
    start: '00:00',
    end: '23:59'
  }

  const geozoneName = geozone.name;
  const points = geozone.wkt.split('((')[1].split('))')[0].split(',').map((item) => {
    const trimmed = item.trim();
    return {
      lat: parseFloat(trimmed.split(' ')[1]),
      lng: parseFloat(trimmed.split(' ')[0]),
    };
  });
  points.splice(points.length - 1, 1);
  const position = [(points[0].lat + points[points.length - 1].lat) / 2, (points[0].lng + points[points.length - 1].lng) / 2];
  return {
    days,
    ...days,
    geozoneName,
    points,
    position,
    existing: true,
    times: times,
    id: geozone.id,
  };
};

@translate(['geozones'], { wait: false })
@connect(state => ({ geozone: state.geozones.currentGeozone, currentDeviceStats: state.devices.currentDeviceStats }), dispatch => ({
  toggleGeozoneEditor() {
    return dispatch(toggleGeozoneEditor());
  },
  createGeozone(params) {
    return dispatch(createGeozoneClicked({ ...params }));
  },
  updateGeozone(params) {
    return dispatch(updateGeozoneClicked({ ...params }));
  },
}))
export class GeozonePage extends Modal {
  constructor(props) {
    super(props);
    const { currentDeviceStats } = this.props
    this.state = {
      points: [],
      position: [currentDeviceStats.lat / 10000000, currentDeviceStats.lon / 10000000],
      editing: false,
      errors: {},
      geozoneName: '',
      days: {},
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
      sun: false,
      times: {
        start: '00:00:00',
        end: '23:59:59'
      },
    };
    if (this.props.geozone.id !== -1) {
      this.state = {
        ...this.state,
        ...convertGeozoneToState(this.props.geozone),
      };
    }

    this.calculatePosition();
  }
  componentDidMount() {
    this.validate();
  }
  handlePointRemove = (index) => {
    this.setState((prevState) => {
      const points = [...prevState.points];
      points.splice(index, 1);
      return {
        points,
      };
    });
  };
  handlePointAdd = (e) => {
    this.setState(prevState => ({
      points: [...prevState.points, e.latlng],
    }), this.validate);
  };
  handlePointUpdate = (e, i) => {
    this.setState((prevState) => {
      const points = [...prevState.points];
      points[i] = e.target._latlng;
      return {
        points,
      };
    });
    this.dragging = false;
  };
  handlePointDragStart = (e) => {
    this.dragging = true;
    this.polyline.remove();
    this.polyline = null;
  };
  handlePointsReset = (e) => {
    e.preventDefault();
    this.setState({
      points: [],
    });
    if (this.polyline) {
      this.polyline.remove();
      this.polyline = null;
    }
  };
  toggleModal = (e) => {
    e.preventDefault();
    this.props.toggleGeozoneEditor();
  };
  handleEditingChange = (e) => {
    e.preventDefault();
    if (this.state.editing && this.polyline) {
      this.polyline.remove();
      this.polyline = null;
    }

    this.setState({
      editing: !this.state.editing,
    });
  };
  handleGeozoneNameChange = (e) => {
    this.setState({
      geozoneName: e.target.value,
    }, this.validate);
  };
  handleTimeChange = (day, timeKey, value) => {
    this.setState(state => ({
      [day]: {
        ...state[day],
        [timeKey]: value,
      },
    }), this.validate);
  };
  handleDayChange = (day) => {
    this.setState(state => {
      if (state[day]) {
        return {
          [day]: undefined,
        }
      }
      return ({
        [day]: {
          from: this.state.times.start || '00:00:00',
          to: this.state.times.end || '23:59:59',
        },
      })
    }, this.validate);
  };
  handleSubmit = (e) => {
    e.preventDefault();
    console.log(this.state)
    if (this.state.errors.name || this.state.errors.days || this.state.errors.points) {
      this.setState({
        touched: true,
      });
      return;
    }
    const lastPoint = `${this.state.points[0].lng} ${this.state.points[0].lat}`;
    const wkt = `POLYGON ((${this.state.points.map(x => (`${x.lng} ${x.lat}`)).join(', ')}, ${lastPoint}))`;
    if (this.state.existing) {
      this.props.updateGeozone({
        ...this.state,
        wkt,
        name: this.state.geozoneName,
      });
    } else {
      this.props.createGeozone({
        ...this.state,
        wkt,
        name: this.state.geozoneName,
      });
    }
  };
  handleMapRef = (e) => {
    if (e && e.leafletElement) {
      this.map = e.leafletElement;
      this.polyline = null;
      this.map.on('mousemove', (mouseEvent) => {
        const { points, editing } = this.state;
        if (points.length > 2 && editing && !this.dragging) {
          if (this.polyline) {
            this.polyline.setLatLngs([points[0], mouseEvent.latlng, points[points.length - 1]]);
            this.polyline.redraw();
          } else {
            this.polyline = L.polyline([points[0], mouseEvent.latlng, points[points.length - 1]]);
            this.polyline.addTo(this.map);
          }
        }
      });
    }
  };
  validate = () => {
    const errors = {};
    errors.name = this.state.geozoneName.trim() === '';
    const weekdaysArray = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    errors.days = !weekdaysArray.reduce((prev, item) => {
      if (this.state[item]) {
        return true;
      }
      return prev || false;
    }, false);
    errors.points = this.state.points.length < 3;
    if (Object.keys(errors).length !== 0 || Object.keys(this.state.errors).length !== 0) {
      this.setState({
        errors,
      });
    }
  };
  calculatePosition = () => {
    const { points } = this.state;

    if (points.length > 2) {
      this.position = this.getCenter(points)
    } else {
      this.position = this.state.position;
    }
  };

  getCenter = (points) => {
    console.log(points)
    if (points.length == 1)
    {
      return points[0]
    }

    let x = 0;
    let y = 0;
    let z = 0;


    points.forEach( (point) => {
      var latitude = point.lat * Math.PI / 180;
      var longitude = point.lng * Math.PI / 180;
      x += Math.cos(latitude) * Math.cos(longitude);
      y += Math.cos(latitude) * Math.sin(longitude);
      z += Math.sin(latitude);
    })

    let total = points.length;

    x = x / total;
    y = y / total;
    z = z / total;

    var centralLongitude = Math.atan2(y, x);
    var centralSquareRoot = Math.sqrt(x * x + y * y);
    var centralLatitude = Math.atan2(z, centralSquareRoot);

    return [centralLatitude * 180 / Math.PI, centralLongitude * 180 / Math.PI];
  }

  handleTimeSlider = (times) => {
    this.setState({ times })
    WEEKDAYS_ARRAY.map((day, i) => {
      if (this.state[day]){
        this.setState({
          [day]: {
            from: times.start,
            to: times.end,
          }
        }
        , this.validate)
      }
    })
  }
  handleDayCheckboxes = ( days ) => {
    this.setState({ days })
  }

  render() {
    const { t } = this.props;
    const { points, editing, geozoneName, from, to, mon, tue, wed, thu, fri, sat, sun } = this.state;
    const drawText = points.length > 0 ? t('geozones:start_editing') : t('geozones:start_drawing');
    const stopDrawText = t('geozones:stop_drawing');
    const position = this.position;

    return (
      <div className="popapWrapper popapDefaultWrapper active">
        <div ref="modal" className="popup garage-editor">
          <a role="button" tabIndex={-1} onClick={this.toggleModal} className="popup__close" />
          <div className="popup__content garage-editor__content">
            <div className="garage-editor__map">
              <div className="garage-editor__map-wrapper map-editing">
                <GeozoneMap
                  points={points}
                  position={position}
                  editing={editing}
                  pointLatLng={this.state.currLatLng || points[points.length - 1] || {
                    lat: position[0],
                    lng: position[1],
                  }}
                  pointRemoveHandler={this.handlePointRemove}
                  pointUpdateHandler={this.handlePointUpdate}
                  pointDragStartHandler={this.handlePointDragStart}
                  pointAddHandler={this.handlePointAdd}
                  handleRef={this.handleMapRef}
                />
              </div>
            </div>
            <div className="garage-editor__panel">
              <a
                role="presentation"
                className={cx('button', 'button_type_action', 'button_action_draw', { button_state_active: editing })}
                onClick={this.handleEditingChange}
              >
                {editing ? stopDrawText : drawText}
              </a>

              {points.length > 0 && editing && <a
                role="presentation"
                className="button button_type_clear"
                onClick={this.handlePointsReset}
              >{t('geozones:clear_marks')}</a>
              }
            </div>
          </div>
          <div className="popup__aside garage-editor__aside">
            <form className="region-form" action="#">
              <div className="region-form__inner">
                <FloatingLabelInput
                  value={geozoneName}
                  style={{ marginTop: 15 }}
                  onChange={this.handleGeozoneNameChange}
                  label={t('geozones:name_placeholder')}
                  /*error={this.state.touched && this.state.errors.name && t('geozones:name_not-empty')}*/
                />
                <div className="error-msg" style={{ marginTop: -15, height: 40 }}>
                  {this.state.touched && this.state.errors.name && <span>{t('geozones:name_not-empty')}</span>}
                </div>
                <div className="timerange-title" style={{ fontWeight: 'bold', marginBottom: 15 }}>{t('geozones:times__title')}</div>
                <div className="current-time-range">{`${this.state.times.start} - ${this.state.times.end}`}</div>
                <TimeRangeSlider
                  format={24}
                  maxValue={"23:59"}
                  minValue={"00:00"}
                  value={this.state.times}
                  onChange={this.handleTimeSlider} />
                <ul className="time-range-hint">
                  <li>1:00</li>
                  <li>6:00</li>
                  <li>12:00</li>
                  <li>18:00</li>
                  <li>23:00</li>
                </ul>
                <TimesList
                  changeDayHandler={this.handleDayChange}
                  mon={mon}
                  tue={tue}
                  wed={wed}
                  thu={thu}
                  fri={fri}
                  sat={sat}
                  sun={sun}
                />
                <div className="error-msg">
                  {this.state.touched && this.state.errors.days && <span>{t('geozones:days_not-empty')}</span>}
                </div>
              </div>
              <div className="error-msg">
                {this.state.touched && this.state.errors.points && <span>{t('geozones:points_not-empty')}</span>}
              </div>
              <footer className="region-form__footer">
                <button type="button" onClick={this.handleSubmit} className="button">{t('geozones:save')}</button>
              </footer>
            </form>
          </div>
        </div>
        <a onClick={this.toggleModal} className="overlay" />
      </div>);
  }
}

GeozonePage.WrappedComponent.propTypes = {
  position: PropTypes.arrayOf(PropTypes.number),
  geozone: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    wkt: PropTypes.string,
    weekly: PropTypes.object,
  }).isRequired,
  createGeozone: PropTypes.func.isRequired,
  updateGeozone: PropTypes.func.isRequired,
  toggleGeozoneEditor: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

GeozonePage.WrappedComponent.defaultProps = {
  position: [55.751244, 37.618423],
};



// WEBPACK FOOTER //
// ./src/components/Modal/GeozonesModal/GeozoneCreatePage.js